import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { AreaView } from '../../../../../services/areas/areas.service';

type InlineMode = 'add' | 'rename';

type UseAreaBarControllerParams = {
  areas: AreaView[];
  activeId: number | null;
  editing: boolean;
  onAdd: (name: string) => void;
  onRename: (areaId: number, name: string) => void;
  onReorder: (areaId: number, otherAreaId: number) => void;
};

export function useAreaBarController({
  areas,
  activeId,
  editing,
  onAdd,
  onRename,
  onReorder,
}: UseAreaBarControllerParams) {
  const [inlineMode, setInlineMode] = useState<InlineMode | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inlineMode !== null) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [inlineMode]);

  // Server-persisted order (sortIndex), shared live across devices via the AreasEvents SSE echo.
  const orderedAreas = useMemo(
    () => [...areas].sort((first, second) => first.sortIndex - second.sortIndex),
    [areas],
  );

  const visibleAreas = editing ? orderedAreas : orderedAreas.filter((area) => area.isActive);
  const orderedAreasLength = orderedAreas.length;
  const activeArea = areas.find((area) => area.id === activeId) ?? null;
  const activeOrderIndex = activeId === null ? -1 : orderedAreas.findIndex((area) => area.id === activeId);

  const closeInline = () => {
    setInlineMode(null);
    setDraft('');
  };

  const openAdd = () => {
    setDraft('');
    setInlineMode('add');
  };

  const openRename = () => {
    if (!activeArea) {
      return;
    }
    setDraft(activeArea.name);
    setInlineMode('rename');
  };

  const confirmInline = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      closeInline();
      return;
    }
    if (inlineMode === 'add') {
      onAdd(trimmed);
    } else if (inlineMode === 'rename' && activeArea) {
      onRename(activeArea.id, trimmed);
    }
    closeInline();
  };

  const moveActiveArea = (direction: -1 | 1) => {
    if (activeId === null) {
      return;
    }
    const fromIndex = orderedAreas.findIndex((area) => area.id === activeId);
    const toIndex = fromIndex + direction;
    if (fromIndex === -1 || toIndex < 0 || toIndex >= orderedAreas.length) {
      return;
    }
    onReorder(activeId, orderedAreas[toIndex].id);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      confirmInline();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeInline();
    }
  };

  return {
    inputRef,
    inlineMode,
    draft,
    setDraft,
    visibleAreas,
    orderedAreasLength,
    activeArea,
    activeOrderIndex,
    closeInline,
    openAdd,
    openRename,
    confirmInline,
    moveActiveArea,
    handleInputKeyDown,
  };
}
